import { refreshAllocationMasterData } from "./allocationMasterDataService";
import { refreshMaterialMasterData } from "./materialMasterDataService";
import { refreshStorageOwnershipData } from "./storageOwnershipDataService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import {
  cacheMasterDataDraftSummaryIndexedDb,
  clearMasterDataDraftSummaryIndexedDb,
  deletePendingMasterDataMutation,
  getPendingMasterDataMutations,
  readMasterDataDraftSummaryIndexedDb,
  updatePendingMasterDataMutationState,
  type PendingMasterDataMutation,
} from "./pendingMasterDataDraftService";

export type MasterDataKind = "material" | "allocation" | "ownership";
export type MasterDataDraftOperation = "created" | "updated" | "deleted";

export interface MasterDataDraft {
  id: number;
  kind: MasterDataKind;
  identity_key: string;
  target_id: number | null;
  operation: MasterDataDraftOperation;
  revision: number;
  baseline_data: Record<string, unknown> | null;
  draft_data: Record<string, unknown> | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  local_pending_key?: string;
  local_sync_state?: "pending" | "conflict" | "error";
  local_sync_error?: string | null;
}

export interface MasterDataDraftSummary {
  kind: MasterDataKind;
  total: number;
  new_entries: number;
  changed_entries: number;
  removed_entries: number;
  oldest_created_at: string | null;
  revision: string | null;
  drafts: MasterDataDraft[];
  source?: "server" | "local-cache" | "server+local-pending" | "local-pending";
  local_pending_count?: number;
}

export interface MasterDataDraftReleaseResult {
  kind: MasterDataKind;
  version_number: number;
  total_entries: number;
  new_entries: number;
  changed_entries: number;
  removed_entries: number;
  activated_at: string | null;
}

export interface PendingMasterDataSyncResult {
  attempted: number;
  synced: number;
  remaining: number;
  conflicts: number;
  errors: number;
}

const LEGACY_CACHE_PREFIX = "materialentnahme:master-data-drafts:";
let pendingSyncPromise: Promise<PendingMasterDataSyncResult> | null = null;

function legacyCacheKey(kind: MasterDataKind): string { return `${LEGACY_CACHE_PREFIX}${kind}`; }

async function cacheSummary(value: MasterDataDraftSummary): Promise<void> {
  await cacheMasterDataDraftSummaryIndexedDb(value.kind, { ...value, source: "local-cache", local_pending_count: 0 }).catch(() => undefined);
  // Legacy localStorage is only a migration fallback. New writes live in IndexedDB.
  try { localStorage.removeItem(legacyCacheKey(value.kind)); } catch { /* optional */ }
}

async function readCached(kind: MasterDataKind): Promise<MasterDataDraftSummary | null> {
  const indexed = await readMasterDataDraftSummaryIndexedDb<MasterDataDraftSummary>(kind).catch(() => null);
  if (indexed) return { ...indexed, kind, source: "local-cache" };
  try {
    const raw = localStorage.getItem(legacyCacheKey(kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MasterDataDraftSummary;
    const migrated = { ...parsed, kind, source: "local-cache" as const };
    await cacheMasterDataDraftSummaryIndexedDb(kind, migrated).catch(() => undefined);
    try { localStorage.removeItem(legacyCacheKey(kind)); } catch { /* optional */ }
    return migrated;
  } catch { return null; }
}

async function clearCached(kind: MasterDataKind): Promise<void> {
  await clearMasterDataDraftSummaryIndexedDb(kind).catch(() => undefined);
  try { localStorage.removeItem(legacyCacheKey(kind)); } catch { /* optional */ }
}

async function detail(response: Response, fallback: string): Promise<string> {
  try { const body = await response.json() as { detail?: string }; return body.detail || fallback; } catch { return fallback; }
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function pendingMatchesServer(pending: PendingMasterDataMutation, server: MasterDataDraft): boolean {
  const effectiveOperation = server.operation === "created" && pending.operation === "updated" ? "created" : pending.operation;
  if (server.operation !== effectiveOperation) return false;
  if (pending.operation === "deleted") return server.operation === "deleted";
  return canonicalJson(server.draft_data ?? null) === canonicalJson(pending.payload ?? null);
}

function mergePending(base: MasterDataDraftSummary | null, pending: PendingMasterDataMutation[], kind: MasterDataKind): MasterDataDraftSummary {
  const serverDrafts = base?.drafts ?? [];
  const byIdentity = new Map(serverDrafts.map((draft) => [draft.identity_key, { ...draft }]));
  pending.forEach((item, index) => {
    const server = byIdentity.get(item.identity_key);
    const operation: MasterDataDraftOperation = server?.operation === "created" && item.operation === "updated" ? "created" : item.operation;
    byIdentity.set(item.identity_key, {
      id: server?.id ?? -(index + 1),
      kind,
      identity_key: item.identity_key,
      target_id: server?.target_id ?? (typeof item.target === "number" ? item.target : null),
      operation,
      revision: server?.revision ?? item.draft_revision ?? 1,
      baseline_data: server?.baseline_data ?? item.baseline_data ?? null,
      draft_data: item.operation === "deleted" ? null : item.payload,
      created_by_user_id: server?.created_by_user_id ?? item.owner_user_id,
      created_at: server?.created_at ?? item.created_at,
      updated_at: item.updated_at,
      local_pending_key: item.key,
      local_sync_state: item.sync_state,
      local_sync_error: item.last_error,
    });
  });
  const drafts = [...byIdentity.values()].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return {
    kind,
    total: drafts.length,
    new_entries: drafts.filter((draft) => draft.operation === "created").length,
    changed_entries: drafts.filter((draft) => draft.operation === "updated").length,
    removed_entries: drafts.filter((draft) => draft.operation === "deleted").length,
    oldest_created_at: drafts.length ? drafts[0].created_at : null,
    revision: drafts.length ? drafts.reduce((max, draft) => draft.updated_at > max ? draft.updated_at : max, drafts[0].updated_at) : null,
    drafts,
    source: pending.length
      ? (base?.source === "server" ? "server+local-pending" : base ? "local-cache" : "local-pending")
      : (base?.source ?? "local-cache"),
    local_pending_count: pending.length,
  };
}

const SERVER_SUMMARY_DEDUPE_MS = 1_000;
const serverSummaryMemo = new Map<MasterDataKind, { at: number; value: MasterDataDraftSummary }>();
const serverSummaryInflight = new Map<MasterDataKind, Promise<MasterDataDraftSummary>>();

async function fetchServerSummary(kind: MasterDataKind, forceRefresh = false): Promise<MasterDataDraftSummary> {
  const now = Date.now();
  const cached = serverSummaryMemo.get(kind);
  if (!forceRefresh && cached && now - cached.at <= SERVER_SUMMARY_DEDUPE_MS) return cached.value;
  if (!forceRefresh) {
    const inflight = serverSummaryInflight.get(kind);
    if (inflight) return inflight;
  }
  const request = (async () => {
    const response = await fetchWithTimeout(`/api/admin/master-drafts/${kind}`, { credentials: "include", cache: "no-store", headers: { Accept: "application/json" } }, 10_000);
    if (!response.ok) throw new Error(await detail(response, "Offene Stammdatenänderungen konnten nicht geladen werden."));
    const value = await response.json() as MasterDataDraftSummary;
    const normalized = { ...value, source: "server" as const, local_pending_count: 0 };
    serverSummaryMemo.set(kind, { at: Date.now(), value: normalized });
    return normalized;
  })();
  if (!forceRefresh) serverSummaryInflight.set(kind, request);
  try { return await request; }
  finally { if (serverSummaryInflight.get(kind) === request) serverSummaryInflight.delete(kind); }
}

export async function fetchMasterDataDraftSummary(kind: MasterDataKind, allowCached = true): Promise<MasterDataDraftSummary> {
  const pending = await getPendingMasterDataMutations(kind).catch(() => []);
  try {
    const server = await fetchServerSummary(kind, !allowCached);
    await cacheSummary(server);
    return mergePending(server, pending, kind);
  } catch (error) {
    if (allowCached) {
      const cached = await readCached(kind);
      if (cached || pending.length) return mergePending(cached, pending, kind);
    }
    throw error;
  }
}

function apiMutationPayload(pending: PendingMasterDataMutation): Record<string, unknown> | null {
  if (!pending.payload) return null;
  if (pending.kind === "allocation") {
    const { entry_key: _entryKey, ...payload } = pending.payload;
    void _entryKey;
    return payload;
  }
  if (pending.kind === "ownership") {
    const { key: _key, ...payload } = pending.payload;
    void _key;
    return payload;
  }
  return pending.payload;
}

function mutationRequest(pending: PendingMasterDataMutation): { url: string; method: string; body?: Record<string, unknown> | null } {
  if (pending.kind === "material") {
    if (pending.operation === "created") return { url: "/api/material-master/admin/materials", method: "POST", body: apiMutationPayload(pending) };
    return { url: `/api/material-master/admin/materials/${encodeURIComponent(String(pending.target ?? pending.identity_key))}`, method: pending.operation === "deleted" ? "DELETE" : "PATCH", body: apiMutationPayload(pending) };
  }
  if (pending.kind === "allocation") {
    if (pending.operation === "created") return { url: "/api/allocation-master/admin/allocations", method: "POST", body: apiMutationPayload(pending) };
    if (typeof pending.target !== "number") throw new Error("Lokale Kontierungsänderung besitzt keine Server-ID.");
    return { url: `/api/allocation-master/admin/allocations/${pending.target}`, method: pending.operation === "deleted" ? "DELETE" : "PATCH", body: apiMutationPayload(pending) };
  }
  if (pending.operation === "created") return { url: "/api/storage-ownership/admin/ownerships", method: "POST", body: apiMutationPayload(pending) };
  return { url: `/api/storage-ownership/admin/ownerships/${encodeURIComponent(String(pending.target ?? pending.identity_key))}`, method: pending.operation === "deleted" ? "DELETE" : "PATCH", body: apiMutationPayload(pending) };
}

async function syncOnePending(pending: PendingMasterDataMutation): Promise<"synced" | "conflict" | "error"> {
  let serverSummary: MasterDataDraftSummary;
  try { serverSummary = await fetchServerSummary(pending.kind); }
  catch (error) {
    await updatePendingMasterDataMutationState(pending.key, "pending", error instanceof Error ? error.message : "Server nicht erreichbar.");
    return "error";
  }
  const matchingDraft = serverSummary.drafts.find((draft) => draft.identity_key === pending.identity_key);
  if (matchingDraft && pendingMatchesServer(pending, matchingDraft)) {
    await deletePendingMasterDataMutation(pending.key);
    return "synced";
  }

  const request = mutationRequest(pending);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (request.body !== undefined && request.body !== null) headers["Content-Type"] = "application/json";
  if (pending.draft_revision !== null) headers["X-Master-Draft-Revision"] = String(pending.draft_revision);
  if (pending.master_version !== null) headers["X-Master-Data-Version"] = String(pending.master_version);

  try {
    const response = await fetchWithTimeout(request.url, {
      method: request.method,
      credentials: "include",
      headers,
      body: request.body !== undefined && request.body !== null ? JSON.stringify(request.body) : undefined,
    }, 10_000);
    if (response.ok) {
      await deletePendingMasterDataMutation(pending.key);
      return "synced";
    }
    const message = await detail(response, response.status === 409 ? "Stammdaten-Konflikt. Bitte den aktuellen Serverstand prüfen." : "Lokale Stammdatenänderung konnte nicht synchronisiert werden.");
    if (response.status === 409 || response.status === 422) {
      await updatePendingMasterDataMutationState(pending.key, "conflict", message);
      return "conflict";
    }
    await updatePendingMasterDataMutationState(pending.key, "error", message);
    return "error";
  } catch (error) {
    await updatePendingMasterDataMutationState(pending.key, "pending", error instanceof Error ? error.message : "Server nicht erreichbar.");
    return "error";
  }
}

export async function syncPendingMasterDataDrafts(kind?: MasterDataKind): Promise<PendingMasterDataSyncResult> {
  // A single mutex intentionally flushes all masterdata kinds. The optional kind
  // remains part of the public call shape; release gates below still check only
  // the requested kind so unrelated pending drafts never block a release.
  void kind;
  if (pendingSyncPromise) return pendingSyncPromise;
  pendingSyncPromise = (async () => {
    const pending = await getPendingMasterDataMutations().catch(() => []);
    if (!pending.length || navigator.onLine === false) return { attempted: 0, synced: 0, remaining: pending.length, conflicts: pending.filter((item) => item.sync_state === "conflict").length, errors: pending.filter((item) => item.sync_state === "error").length };
    let synced = 0; let conflicts = 0; let errors = 0;
    for (const item of pending) {
      const result = await syncOnePending(item);
      if (result === "synced") synced += 1;
      else if (result === "conflict") conflicts += 1;
      else errors += 1;
    }
    const remaining = (await getPendingMasterDataMutations().catch(() => [])).length;
    const affectedKinds = new Set(pending.map((item) => item.kind));
    for (const affectedKind of affectedKinds) {
      const fresh = await fetchServerSummary(affectedKind).catch(() => null);
      if (fresh) await cacheSummary(fresh);
      window.dispatchEvent(new CustomEvent("materialentnahme:master-data-drafts-updated", { detail: { kind: affectedKind } }));
    }
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    return { attempted: pending.length, synced, remaining, conflicts, errors };
  })();
  try { return await pendingSyncPromise; }
  finally { pendingSyncPromise = null; }
}

export function installPendingMasterDataDraftSyncTriggers(): () => void {
  const run = () => { if (navigator.onLine !== false) void syncPendingMasterDataDrafts(); };
  const visible = () => { if (document.visibilityState === "visible") run(); };
  const timer = window.setTimeout(run, 1400);
  window.addEventListener("online", run);
  window.addEventListener("pageshow", run);
  window.addEventListener("focus", run);
  document.addEventListener("visibilitychange", visible);
  return () => {
    window.clearTimeout(timer);
    window.removeEventListener("online", run);
    window.removeEventListener("pageshow", run);
    window.removeEventListener("focus", run);
    document.removeEventListener("visibilitychange", visible);
  };
}

export async function releaseMasterDataDrafts(kind: MasterDataKind): Promise<MasterDataDraftReleaseResult> {
  await syncPendingMasterDataDrafts(kind);
  const remainingForKind = await getPendingMasterDataMutations(kind).catch(() => []);
  if (remainingForKind.length > 0) {
    throw new Error(remainingForKind.some((item) => item.sync_state === "conflict")
      ? "Mindestens eine lokal gespeicherte Stammdatenänderung steht im Konflikt mit dem Server. Bitte neu laden und den Konflikt prüfen."
      : "Lokale Stammdatenänderungen konnten noch nicht vollständig mit dem Server synchronisiert werden. Die Version wurde nicht freigegeben.");
  }
  const response = await fetch(`/api/admin/master-drafts/${kind}/release`, { method: "POST", credentials: "include", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(await detail(response, "Version konnte nicht freigegeben werden."));
  const result = await response.json() as MasterDataDraftReleaseResult;
  await clearCached(kind);
  await refreshMasterDataKind(kind).catch(() => undefined);
  dispatchMasterDataReleased(kind);
  return result;
}

export async function discardMasterDataDraft(kind: MasterDataKind, draftId: number, revision: number, localPendingKey?: string): Promise<void> {
  if (localPendingKey) {
    await deletePendingMasterDataMutation(localPendingKey);
    window.dispatchEvent(new CustomEvent("materialentnahme:master-data-drafts-updated", { detail: { kind } }));
    window.dispatchEvent(new Event("materialentnahme:status-refresh"));
    return;
  }
  const response = await fetch(`/api/admin/master-drafts/${kind}/${draftId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-Master-Draft-Revision": String(revision) },
  });
  if (!response.ok) throw new Error(await detail(response, "Entwurf konnte nicht verworfen werden."));
  await fetchMasterDataDraftSummary(kind, false).catch(() => undefined);
  window.dispatchEvent(new CustomEvent("materialentnahme:master-data-drafts-updated", { detail: { kind } }));
}

export async function refreshMasterDataKind(kind: MasterDataKind): Promise<void> {
  if (kind === "material") await refreshMaterialMasterData();
  else if (kind === "allocation") await refreshAllocationMasterData();
  else await refreshStorageOwnershipData();
}

export function dispatchMasterDataReleased(kind: MasterDataKind): void {
  window.dispatchEvent(new CustomEvent("materialentnahme:master-data-released", { detail: { kind } }));
  window.dispatchEvent(new CustomEvent("materialentnahme:master-data-drafts-updated", { detail: { kind } }));
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  if (kind === "material") window.dispatchEvent(new Event("materialentnahme:master-data-status-updated"));
  if (kind === "allocation") window.dispatchEvent(new Event("materialentnahme:allocation-data-status-updated"));
  if (kind === "ownership") window.dispatchEvent(new Event("materialentnahme:ownership-data-updated"));
}

export function cacheMasterDataDraftSummary(value: MasterDataDraftSummary): void {
  void cacheSummary(value);
}
